import React, { useMemo } from 'react';

import { Flex, Space, Table, TableColumnType, TableProps, Tag } from 'antd-v5';
import { DateTime } from 'luxon';
import { MdCheckCircle } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { Text } from 'scouting-ui/lib';
import vars from 'scouting-ui/lib/variables';

import type { Event } from '@appTypes/esb';
import { ConditionalRender } from '@shared/components';

import styles from '../BulkRemovePage.scss';

export interface EventRow {
  name: string;
  id: number;
  key: string;
  isMultiUnit: boolean;
  startDate: DateTime;
  endDate: DateTime;
  units: Event['units'];
}

type SelectionType = TableProps<EventRow>['rowSelection'];

interface EventsTableProps {
  events: Event[];
  onChange: (selectedRowKeys: React.Key[], selectedRows: EventRow[]) => void;
}

export default function EventsTable(props: EventsTableProps) {
  const { events, onChange } = props;

  const eventItems: EventRow[] =
    events?.map(event => ({
      id: event.id,
      key: `${event.id}`,
      name: event.name,
      isMultiUnit: event.units?.length > 1,
      startDate: DateTime.fromISO(event.startDate),
      endDate: DateTime.fromISO(event.endDate),
      units: event.units,
    })) || [];

  const rowSelection: SelectionType = {
    onChange,
    getCheckboxProps: (record: EventRow) => ({
      name: `event_${record.id}`,
    }),
  };

  const columns = useMemo(
    () =>
      [
        {
          dataIndex: 'name',
          title: <FormattedMessage id="bulkDeleteInviteePage.table.event" />,
          width: 250,
          render: (name: string, record: EventRow) => {
            const isMultiUnit = record.isMultiUnit;
            return (
              <Flex vertical gap="small">
                <Text>{name}</Text>
                <ConditionalRender value={isMultiUnit}>
                  <Space wrap size={2}>
                    {record.units?.map(unit => (
                      <Tag className={styles.unitTag} key={unit.unitId}>
                        {unit.unitFullName}
                      </Tag>
                    ))}
                  </Space>
                </ConditionalRender>
              </Flex>
            );
          },
        },
        {
          dataIndex: 'startDate',
          title: <FormattedMessage id="shared.date.startDate" />,
          width: 100,
          render: (value: DateTime) => (
            <>{value.toLocaleString(DateTime.DATETIME_SHORT)}</>
          ),
          defaultSortOrder: 'descend',
          sorter: (rowA, rowB) =>
            rowA.startDate.toMillis() - rowB.startDate.toMillis(),
        },
        {
          dataIndex: 'endDate',
          title: <FormattedMessage id="shared.date.endDate" />,
          width: 100,
          render: (value: DateTime) => (
            <>{value.toLocaleString(DateTime.DATETIME_SHORT)}</>
          ),
          sorter: (rowA, rowB) =>
            rowA.endDate.toMillis() - rowB.endDate.toMillis(),
        },
        {
          dataIndex: 'isMultiUnit',
          title: (
            <FormattedMessage id="bulkDeleteInviteePage.table.multiUnit" />
          ),
          defaultSortOrder: 'descend',
          width: 50,
          sorter: (rowA, rowB) =>
            rowA.isMultiUnit === rowB.isMultiUnit
              ? 0
              : rowA.isMultiUnit
              ? 1
              : -1,
          render: isMultiUnit => {
            if (!isMultiUnit) return null;
            return (
              <Flex align="center" justify="center">
                <MdCheckCircle color={vars['color-success']} />
              </Flex>
            );
          },
        },
      ] satisfies TableColumnType<EventRow>[],
    [],
  );

  return (
    <Table
      rowSelection={rowSelection}
      dataSource={eventItems}
      pagination={{
        total: eventItems.length,
        pageSizeOptions: [10, 20, 50],
        defaultPageSize: 10,
        showTotal: total => (
          <Text>
            <FormattedMessage
              id="bulkDeleteInviteePage.table.total"
              values={{ count: total }}
            />
          </Text>
        ),
        showSizeChanger: true,
      }}
      showSorterTooltip
      columns={columns}
    />
  );
}
